import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { ConversationList } from '../components/conversations/ConversationList';
import { UnifiedThread } from '../components/conversations/UnifiedThread';
import { WhatsAppAnalytics } from '../components/whatsapp/WhatsAppAnalytics';
import { WhatsAppLead, UnifiedMessage } from '../types/whatsapp';
import { MessageSquareIcon, BarChart2Icon, MapPinIcon } from 'lucide-react';
import { useViewMode } from '../context/ViewModeContext';
import { ALL_CONVERSATION_LEADS } from '../data/mockData';
const MOCK_MESSAGES: UnifiedMessage[] = [{
  id: '1',
  content: 'Lead assigned to dealer',
  timestamp: 'Yesterday 9:00 AM',
  from: 'system',
  status: 'completed',
  type: 'event',
  channel: 'system'
}, {
  id: '2',
  content: 'Hi! Thanks for your interest in our vehicles. Here is the brochure you requested.',
  timestamp: 'Yesterday 9:05 AM',
  from: 'bot',
  status: 'read',
  type: 'document',
  channel: 'auto-trigger'
}, {
  id: '3',
  content: 'Thanks! Can I schedule a test drive for this weekend?',
  timestamp: 'Yesterday 10:15 AM',
  from: 'customer',
  status: 'read',
  type: 'text',
  channel: 'whatsapp'
}, {
  id: '4',
  content: 'Tried calling you',
  timestamp: 'Yesterday 2:30 PM',
  from: 'dealer',
  status: 'missed',
  type: 'call',
  channel: 'voice',
  metadata: {
    callDuration: '0:00'
  }
}, {
  id: '5',
  content: 'Is the test drive available tomorrow?',
  timestamp: '10:30 AM',
  from: 'customer',
  status: 'delivered',
  type: 'text',
  channel: 'whatsapp'
}, {
  id: '6',
  content: 'Yes, we have slots available at 10 AM and 2 PM. Which one works for you?',
  timestamp: '10:45 AM',
  from: 'dealer',
  status: 'read',
  type: 'text',
  channel: 'whatsapp'
}, {
  id: '7',
  content: '2 PM works perfectly. Please confirm the address.',
  timestamp: '11:00 AM',
  from: 'customer',
  status: 'read',
  type: 'text',
  channel: 'whatsapp'
}];
export function Conversations() {
  const {
    viewMode,
    currentLocation,
    isAllLocationsSelected,
    isSingleLocationSelected,
    selectedBrandLocations,
    getSelectedLocationData
  } = useViewMode();
  const [activeTab, setActiveTab] = useState<'inbox' | 'analytics'>('inbox');
  // Convert mock data to WhatsAppLead format
  const allLeads: WhatsAppLead[] = ALL_CONVERSATION_LEADS.map(lead => ({
    id: lead.id,
    name: lead.name,
    phone: lead.phone,
    source: lead.source as any,
    lastMessage: lead.lastMessage,
    lastMessageTime: lead.lastMessageTime,
    unreadCount: lead.unreadCount,
    aiScore: lead.aiScore,
    status: lead.status,
    tags: lead.tags,
    carModel: lead.carModel,
    dealer: lead.dealer
  }));
  // Filter leads based on view mode and selected locations from context
  const getFilteredLeads = () => {
    if (viewMode === 'store' && currentLocation) {
      return allLeads.filter(lead => {
        const leadData = ALL_CONVERSATION_LEADS.find(l => l.id === lead.id);
        return leadData?.dealerCode === currentLocation.code;
      });
    }
    if (viewMode === 'brand') {
      if (isSingleLocationSelected) {
        const locationCode = selectedBrandLocations[0];
        return allLeads.filter(lead => {
          const leadData = ALL_CONVERSATION_LEADS.find(l => l.id === lead.id);
          return leadData?.dealerCode === locationCode;
        });
      }
      return allLeads;
    }
    return allLeads;
  };
  const filteredLeads = getFilteredLeads();
  const selectedLocation = getSelectedLocationData();
  const [selectedLead, setSelectedLead] = useState<WhatsAppLead>(filteredLeads[0] || allLeads[0]);
  const [messages, setMessages] = useState<UnifiedMessage[]>(MOCK_MESSAGES);
  const handleSendMessage = (content: string, channel: any) => {
    const newMessage: UnifiedMessage = {
      id: Date.now().toString(),
      content,
      timestamp: 'Just now',
      from: 'dealer',
      status: 'sent',
      type: 'text',
      channel
    };
    setMessages([...messages, newMessage]);
  };
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-2124">
      <Sidebar data-id="element-2125" />
      <main className="flex-1 flex flex-col h-screen overflow-hidden" data-id="element-2126">
        <Header data-id="element-2127" />

        <div className="bg-white border-b border-gray-200 px-6 pt-4" data-id="element-2128">
          <div className="flex items-center justify-between" data-id="element-2129">
            <div className="flex space-x-6" data-id="element-2130">
              <button onClick={() => setActiveTab('inbox')} className={`flex items-center pb-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'inbox' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2131">
                <MessageSquareIcon size={18} className="mr-2" data-id="element-2132" />
                Unified Inbox
                {filteredLeads.length > 0 && <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full" data-id="element-2133">
                    {filteredLeads.length}
                  </span>}
              </button>
              <button onClick={() => setActiveTab('analytics')} className={`flex items-center pb-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'analytics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2134">
                <BarChart2Icon size={18} className="mr-2" data-id="element-2135" />
                Analytics & Insights
              </button>
            </div>

            {/* Location indicator - uses global filter from Header */}
            <div className="pb-4" data-id="element-2136">
              {viewMode === 'brand' && isAllLocationsSelected && <span className="text-sm text-gray-500" data-id="element-2137">
                  Showing conversations from all locations
                </span>}
              {viewMode === 'brand' && isSingleLocationSelected && selectedLocation && <span className="inline-flex items-center px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium border border-amber-200" data-id="element-2138">
                    <MapPinIcon size={14} className="mr-1.5" data-id="element-2139" />
                    {selectedLocation.name} • {filteredLeads.length}{' '}
                    conversations
                  </span>}
              {viewMode === 'store' && currentLocation && <span className="inline-flex items-center px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium border border-emerald-200" data-id="element-2140">
                  <MapPinIcon size={14} className="mr-1.5" data-id="element-2141" />
                  {currentLocation.name} • {filteredLeads.length} conversations
                </span>}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden" data-id="element-2142">
          {activeTab === 'inbox' ? <div className="flex h-full" data-id="element-2143">
              <ConversationList leads={filteredLeads} selectedLeadId={selectedLead?.id} onSelectLead={setSelectedLead} data-id="element-2144" />
              <div className="flex-1 h-full" data-id="element-2145">
                {selectedLead ? <UnifiedThread lead={selectedLead} messages={messages} onSendMessage={handleSendMessage} data-id="element-2146" /> : <div className="flex items-center justify-center h-full text-gray-500" data-id="element-2147">
                    <p data-id="element-2148">Select a conversation to view</p>
                  </div>}
              </div>
            </div> : <div className="h-full overflow-y-auto p-6" data-id="element-2149">
              <WhatsAppAnalytics data-id="element-2150" />
            </div>}
        </div>
      </main>
    </div>;
}